import {
  applySnapshot,
  flow,
  getSnapshot,
  SnapshotOrInstance,
  types,
} from "mobx-state-tree";

import api from "services/api";

import { reorder } from "utils/calculation";

import { REORDER_MOVE_TYPES } from "constants/reorder";

const DayBannerImage = types.model({
  id: types.number,
  imageUrl: types.string,
  order: types.number,
});

const DayBannerItem = types.model({
  images: types.array(DayBannerImage),
  name: types.string,
  id: types.number,
});

const DayBannersStore = types
  .model({
    items: types.array(DayBannerItem),
    isLoading: types.optional(types.boolean, false),
    isManaging: types.optional(types.boolean, false),
    isReordering: types.optional(types.boolean, false),
  })
  .actions(self => ({
    getReorderInfo(
      reorderedItems: SnapshotOrInstance<typeof DayBannerImage>[],
      imageId: number,
    ) {
      const imageNewId = reorderedItems.findIndex(
        (item: SnapshotOrInstance<typeof DayBannerImage>) =>
          item.id === imageId,
      );

      switch (imageNewId) {
        case 0:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.TOP_OF_PAGE,
          };
        case reorderedItems.length - 1:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.BOTTOM_OF_PAGE,
          };
        default:
          return {
            afterId: reorderedItems[imageNewId - 1].id,
            moveType: REORDER_MOVE_TYPES.NORMAL,
          };
      }
    },
  }))
  .actions(self => ({
    fetch: flow(function*() {
      self.isLoading = true;

      try {
        const { items } = yield api.get(`/food/day/type/detail/list`);
        applySnapshot(self, { ...self, items });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    addImage: flow(function*(payload: any) {
      self.isManaging = true;

      try {
        yield api.postFormData(`/food/day/type/image/upload`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),

    editImage: flow(function*(payload: any) {
      self.isManaging = true;

      try {
        yield api.putFormData(`/food/day/type/image/${payload.id}`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),

    deleteImage: flow(function*(id: number) {
      self.isManaging = true;

      try {
        yield api.delete(`/food/day/type/image/${id}`);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),

    reorderImages: flow(function*({
      dayTypeId,
      startIndex,
      endIndex,
    }: {
      dayTypeId: number;
      startIndex: number;
      endIndex: number;
    }) {
      self.isReordering = true;
      const { items } = getSnapshot(self);

      try {
        const dayBanners = items.find(({ id }) => id === dayTypeId);
        const dayIndex = items.findIndex(({ id }) => id === dayTypeId);
        const imagesToReorder = dayBanners?.images;

        if (dayBanners && imagesToReorder) {
          const imageId = imagesToReorder[startIndex].id;
          const reorderedImages = reorder(
            imagesToReorder,
            startIndex,
            endIndex,
          );
          const reorderedItems = [...items];
          reorderedItems.splice(dayIndex, 1, {
            ...dayBanners,
            images: reorderedImages,
          });

          applySnapshot(self, { ...self, items: reorderedItems });

          const reorderInfo = self.getReorderInfo(reorderedImages, imageId);

          yield api.post(`/food/day/type/image/move`, {
            id: imageId,
            ...reorderInfo,
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        applySnapshot(self, { ...self, items });
        throw error;
      } finally {
        self.isReordering = false;
      }
    }),

    reset() {
      applySnapshot(self, { items: [] });
    },
  }));

export default DayBannersStore.create();
